<template>
  <div>
    <div class="row mt-2 mb-2"></div>
    <p class="bb-hdg mt-2 mb-3">Poison Details</p>
    <!-- ROW 1: poison type + potency -->
    <div class="row mt-2 mb-2">
      <div class="col-4 form-group">
        <div class="form-floating">
          <v-select
            v-if="!isFieldReadOnly('poison_type')"
            :options="poisonTypeOptions"
            :class="[
              'form-control',
              { 'is-invalid': errorFields.includes('poison_type') },
            ]"
            placeholder="select..."
            :modelValue="localItem.poison_type"
            @update:modelValue="onFieldChanged('poison_type', $event)"
            id="poison_type"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
          <input
            v-else
            readonly
            class="form-control text-center"
            :value="poisonTypeDisplay"
            id="poison_type"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="poison_type"
            >Poison Type</label
          >
        </div>
      </div>
      <div class="col-4 form-group">
        <div
          v-if="isFieldReadOnly('pot')"
          class="form-floating"
        >
          <input
            readonly
            class="form-control text-center"
            :value="potencyDisplay"
            id="pot"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="pot"
            >Potency</label
          >
        </div>
        <CounterEmit
          v-else
          :pass_ref="'pot'"
          :pass_label="'Potency'"
          :pass_count="isNewItem ? (localItem.pot ?? 0) : (activeItem.pot ?? 0)"
          :pass_min="0"
          :pass_max="100"
          :pass_digits="2"
          :invalid="errorFields.includes('pot')"
          @change="onFieldChanged('pot', $event.count)"
        />
      </div>
    </div>
    <!-- ROW 2: effect + delay -->
    <div class="row mt-2 mb-2">
      <div class="col form-group">
        <div class="form-floating">
          <input
            v-if="!isFieldReadOnly('effect')"
            :class="[
              'form-control text-left',
              { 'is-invalid': errorFields.includes('effect') },
            ]"
            :value="localItem.effect"
            id="effect"
            @input="onFieldChanged('effect', $event.target.value)"
          />
          <input
            v-else
            readonly
            class="form-control text-left"
            :value="effectDisplay"
            id="effect"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="effect"
            >Effect</label
          >
        </div>
      </div>
      <div class="col form-group">
        <div class="form-floating">
          <input
            v-if="!isFieldReadOnly('delay')"
            :class="[
              'form-control text-left',
              { 'is-invalid': errorFields.includes('delay') },
            ]"
            :value="localItem.delay"
            id="delay"
            @input="onFieldChanged('delay', $event.target.value)"
          />
          <input
            v-else
            readonly
            class="form-control text-left"
            :value="delayDisplay"
            id="delay"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="delay"
            >Delay</label
          >
        </div>
      </div>
    </div>
    <!-- ROW 3: damage dice + duration -->
    <div class="row mt-2 mb-2">
      <div class="col form-group">
        <div class="form-floating">
          <input
            v-if="!isFieldReadOnly('damage_dice')"
            :class="[
              'form-control text-left',
              { 'is-invalid': errorFields.includes('damage_dice') },
            ]"
            :value="localItem.damage_dice"
            id="damage_dice"
            @input="onFieldChanged('damage_dice', $event.target.value)"
          />
          <input
            v-else
            readonly
            class="form-control text-left"
            :value="damageDiceDisplay"
            id="damage_dice"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="damage_dice"
            >Damage Dice</label
          >
        </div>
      </div>
      <div class="col form-group">
        <div class="form-floating">
          <input
            v-if="!isFieldReadOnly('duration')"
            :class="[
              'form-control text-left',
              { 'is-invalid': errorFields.includes('duration') },
            ]"
            :value="localItem.duration"
            id="duration"
            @input="onFieldChanged('duration', $event.target.value)"
          />
          <input
            v-else
            readonly
            class="form-control text-left"
            :value="durationDisplay"
            id="duration"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="duration"
            >Duration</label
          >
        </div>
      </div>
    </div>
    <!-- ROW 4: resist -->
    <div class="row mt-2 mb-2">
      <div class="col-6 form-group">
        <div class="form-floating">
          <input
            v-if="!isFieldReadOnly('resist')"
            :class="[
              'form-control text-left',
              { 'is-invalid': errorFields.includes('resist') },
            ]"
            :value="localItem.resist"
            id="resist"
            @input="onFieldChanged('resist', $event.target.value)"
          />
          <input
            v-else
            readonly
            class="form-control text-left"
            :value="resistDisplay"
            id="resist"
            disabled
          />
          <label
            class="bb-float-label optional"
            for="resist"
            >Resist</label
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CounterEmit from './CounterEmit.vue'

export default {
  name: 'ItemPoisonTab',
  components: {
    CounterEmit,
  },
  props: {
    activeItem: {
      type: Object,
      required: true,
    },
    localItem: {
      type: Object,
      required: true,
    },
    isNewItem: {
      type: Boolean,
      default: false,
    },
    onFieldChanged: {
      type: Function,
      default: () => {},
    },
    poisonTypes: {
      type: Array,
      default: () => [],
    },
    errorFields: {
      type: Array,
      default: () => [],
    },
    readOnlyFields: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    poisonTypeOptions() {
      return this.poisonTypes
    },
    poisonTypeDisplay() {
      return this.localItem.poison_type ?? this.activeItem.poison_type ?? ''
    },
    effectDisplay() {
      return this.localItem.effect ?? this.activeItem.effect ?? ''
    },
    delayDisplay() {
      return this.localItem.delay ?? this.activeItem.delay ?? ''
    },
    damageDiceDisplay() {
      return this.localItem.damage_dice ?? this.activeItem.damage_dice ?? ''
    },
    durationDisplay() {
      return this.localItem.duration ?? this.activeItem.duration ?? ''
    },
    resistDisplay() {
      return this.localItem.resist ?? this.activeItem.resist ?? ''
    },
    potencyDisplay() {
      return this.localItem.pot ?? this.activeItem.pot ?? 0
    },
  },
  methods: {
    isFieldReadOnly(field) {
      return !!this.readOnlyFields?.[field]
    },
  },
}
</script>
